import { createRoot, Root as ReactRoot } from "react-dom/client";
import { useState, useEffect, Suspense, lazy } from "react";
import "./index.css";
import { ensureStorageVersion } from "./lib/siteReset";
import { initSentry } from "./lib/sentry";
import { SplashScreen } from "./components/pwa/SplashScreen";
import { DCButtonLoader } from "./components/ui/dc-loader";

// Initialize Sentry BEFORE anything else
initSentry();

// Verifica versão do storage e limpa dados antigos se necessário
ensureStorageVersion();

// Lazy load App to prevent dual rendering issues
const App = lazy(() => import("./App"));

// Check if running as installed PWA
const isPWA = 
  window.matchMedia('(display-mode: standalone)').matches ||
  (window.navigator as any).standalone === true ||
  document.referrer.includes('android-app://');

function Root() {
  const [showSplash, setShowSplash] = useState(isPWA);
  const [appReady, setAppReady] = useState(!isPWA);

  // Register service worker message handler
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'SW_ACTIVATED') {
        console.log('[Root] New SW activated, version:', event.data.version);
      }
    };
    
    navigator.serviceWorker.addEventListener('message', handleMessage);
    
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage);
    };
  }, []);

  // Handle splash finish
  const handleSplashFinish = () => {
    setShowSplash(false);
    setAppReady(true);
  };

  // Show splash for PWA
  if (showSplash) {
    return (
      <SplashScreen 
        minDuration={1800} 
        onFinish={handleSplashFinish} 
      />
    );
  }

  // Only render App when ready
  if (!appReady) {
    return null;
  }

  return (
    <Suspense fallback={
      <div className="flex items-center justify-center min-h-screen bg-background">
        <DCButtonLoader size={32} />
      </div>
    }>
      <App />
    </Suspense>
  );
}

// Guard against multiple createRoot calls (happens with HMR and visibility changes)
let root: ReactRoot | null = null;

const container = document.getElementById("root")!;

if (!root) {
  root = createRoot(container);
}

// Render without StrictMode to prevent double-render issues on visibility change
root.render(<Root />);

// Register Firebase Messaging Service Worker
if ('serviceWorker' in navigator) {
  navigator.serviceWorker
    .register('/firebase-messaging-sw.js')
    .then((reg) => console.log('[SW] Firebase registered:', reg.scope))
    .catch((err) => console.error('[SW] Firebase registration failed:', err));
}
